/**
 * Pure client-side helpers for the voice room a viewer should be connected to right now. Mirrors the server's
 * own membership preference (packages/server/src/engine/engine.ts `voiceCredentials`, and
 * packages/server/src/voice/rooms.ts): a team room wins over the shared room while the viewer is assigned to
 * one, and the shared room is used otherwise (docs/ARCHITECTURE.md §9, `VoiceScope`). No I/O here — `useVoice.ts`
 * calls this to decide when to refetch credentials and reconnect.
 */
import type { VoiceRoomAssignment } from '@momentum/shared';

/**
 * Picks the room `participantId` should be connected to from the session's current `rooms` list, preferring
 * their team room (when `teamId` matches one) over the always-reserved shared room. Returns `null` only when
 * the viewer is not (yet) a member of any listed room — e.g. spectators/presenters before the server has
 * added them, or a `participantId` of `null` (no identity yet).
 */
export function selectRoomForViewer(
  rooms: readonly VoiceRoomAssignment[],
  participantId: string | null,
  teamId: string | null,
): VoiceRoomAssignment | null {
  if (!participantId) return null;
  const memberships = rooms.filter((room) => room.participantIds.includes(participantId));
  if (memberships.length === 0) return null;
  const teamRoom = teamId ? memberships.find((room) => room.scope === 'team' && room.teamId === teamId) : undefined;
  if (teamRoom) return teamRoom;
  return memberships.find((room) => room.scope === 'shared') ?? memberships[0];
}

/** Stable string key for a room list, so callers can depend on "did membership actually change" rather than array identity. */
export function roomsKey(rooms: readonly VoiceRoomAssignment[]): string {
  return rooms.map((room) => `${room.roomName}:${room.scope}:${room.teamId ?? ''}:${room.participantIds.join(',')}`).join('|');
}
